import React from 'react'
import AppCombobox from '../AppCombobox'
import AppDropdown from '../AppDropdown'

const DEFAULT_SEMESTER_OPTIONS = [
  { value: 0, label: 'Select Semester' },
  { value: 1, label: 'Sem. I' },
  { value: 2, label: 'Sem. II' },
  { value: 3, label: 'Sem. III' },
  { value: 4, label: 'Sem. IV' },
  { value: 5, label: 'Sem. V' },
  { value: 6, label: 'Sem. VI' },
]

const DEFAULT_TEST_TYPE_OPTIONS = [
  { value: 1, label: 'Regular' },
  { value: 2, label: 'OMR' },
  { value: 3, label: 'Online [PDF]' },
  { value: 4, label: 'Online [Template]' },
]

const DEFAULT_EXAM_TYPE_OPTIONS = [
  { value: '', label: 'Select Exam Type' },
  { value: 'Internal', label: 'Internal' },
  { value: 'External', label: 'External' },
]

const DEFAULT_EXAM_FOR_OPTIONS = [
  { value: '', label: 'Select Exam For' },
  { value: 'Regular', label: 'Regular' },
  { value: 'ATKT', label: 'ATKT' },
]

export default function TestScoringSection({
  // Test Number & Duplicate Flag
  testNo = 0,
  onTestNoChange,
  showIgnoreDuplication = true,
  ignoreDuplication = false,
  onIgnoreDuplicationChange,

  // Marks Scoring
  totalMarks = 100,
  onTotalMarksChange,
  minMarks = 35,
  onMinMarksChange,

  // Department & Group
  department = '',
  onDepartmentChange,
  departmentOptions = [],
  testGroup = '',
  onTestGroupChange,
  testGroupOptions = [],

  // Legacy Classifications
  semester = 0,
  onSemesterChange,
  semesterOptions = DEFAULT_SEMESTER_OPTIONS,

  testType = 1,
  onTestTypeChange,
  testTypeOptions = DEFAULT_TEST_TYPE_OPTIONS,

  examType = '',
  onExamTypeChange,
  examTypeOptions = DEFAULT_EXAM_TYPE_OPTIONS,

  examFor = '',
  onExamForChange,
  examForOptions = DEFAULT_EXAM_FOR_OPTIONS,
}) {
  return (
    <section className="rounded-3xl border border-outline-variant/30 bg-surface-container-lowest shadow-xs">
      {/* ── Section Header ── */}
      <div className="border-b border-outline-variant/20 bg-surface-container-low/60 px-6 py-4 rounded-t-3xl flex items-center gap-3">
        <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-primary/10 text-primary">
          <span className="material-symbols-outlined text-lg">assignment_turned_in</span>
        </div>
        <div>
          <h2 className="font-display text-base font-bold text-on-surface">
            Examination Scoring &amp; Details
          </h2>
          <p className="text-xs text-on-surface-variant">
            Set examination numbers, scoring criteria, and department classifications.
          </p>
        </div>
      </div>

      {/* ── Fields Content ── */}
      <div className="p-6 md:p-8 flex flex-col gap-6 text-xs">
        {/* Row 1: Test No & Duplicate Configuration */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Test No <span className="text-primary font-medium normal-case tracking-normal">(0 = Auto Assign)</span>
            </label>
            <input
              type="number"
              min="0"
              value={testNo}
              onChange={(e) => onTestNoChange && onTestNoChange(Math.max(0, parseInt(e.target.value, 10) || 0))}
              placeholder="0"
              className="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
            />
            <span className="text-[10px] text-on-surface-variant/80">
              Leave as 0 to automatically allocate the next sequential test number.
            </span>
          </div>

          {showIgnoreDuplication && (
            <div className="flex flex-col gap-1.5 pt-6">
              <label className="inline-flex items-center gap-2.5 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={Boolean(ignoreDuplication)}
                  onChange={(e) => onIgnoreDuplicationChange && onIgnoreDuplicationChange(e.target.checked)}
                  className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 accent-primary cursor-pointer"
                />
                <span className="text-xs font-bold text-on-surface">
                  Ignore Duplication
                </span>
              </label>
              <span className="text-[10px] text-on-surface-variant/80 pl-6.5">
                Permits creating another test with the same test number in the selected academic year.
              </span>
            </div>
          )}
        </div>

        {/* Row 2: Marks Scoring */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Total / Max Marks <span className="text-error font-bold">*</span>
            </label>
            <input
              type="number"
              min="1"
              value={totalMarks}
              onChange={(e) => onTotalMarksChange && onTotalMarksChange(Math.max(1, parseInt(e.target.value, 10) || 0))}
              placeholder="100"
              required
              className="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Min / Passing Marks <span className="text-error font-bold">*</span>
            </label>
            <input
              type="number"
              min="0"
              max={totalMarks}
              value={minMarks}
              onChange={(e) => onMinMarksChange && onMinMarksChange(Math.max(0, parseFloat(e.target.value) || 0))}
              placeholder="35"
              required
              className="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface outline-none focus:border-primary focus:ring-2 focus:ring-primary/10 transition-all"
            />
          </div>
        </div>

        {/* Row 3: Department & Test Group */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Department
            </label>
            <AppCombobox
              items={departmentOptions}
              value={department}
              onChange={onDepartmentChange}
              placeholder="Select Department"
              searchPlaceholder="Search departments..."
              emptyText="No departments found"
              allowClear={true}
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Test Group / Series
            </label>
            <AppCombobox
              items={testGroupOptions}
              value={testGroup}
              onChange={onTestGroupChange}
              placeholder="Select Test Group (e.g. Unit Test, Final)"
              searchPlaceholder="Search test groups..."
              emptyText="No test groups found"
              allowClear={true}
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary"
            />
          </div>
        </div>

        {/* Row 4: Semester, Test Type, Exam Type, Exam For */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Semester
            </label>
            <AppDropdown
              options={semesterOptions}
              value={semester}
              onChange={onSemesterChange}
              placeholder="Select Semester"
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Test Type
            </label>
            <AppDropdown
              options={testTypeOptions}
              value={testType}
              onChange={onTestTypeChange}
              placeholder="Select Test Type"
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Exam Type
            </label>
            <AppDropdown
              options={examTypeOptions}
              value={examType}
              onChange={onExamTypeChange}
              placeholder="Select Exam Type"
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary w-full"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-[11px] font-black uppercase tracking-[0.1em] text-on-surface-variant">
              Exam For
            </label>
            <AppDropdown
              options={examForOptions}
              value={examFor}
              onChange={onExamForChange}
              placeholder="Select Exam For"
              triggerClassName="h-10 rounded-xl border border-outline-variant/40 bg-surface-container-low px-3.5 text-xs font-semibold text-on-surface focus:border-primary w-full"
            />
          </div>
        </div>
      </div>
    </section>
  )
}

